import { useState, useRef, useEffect, useCallback } from "react";
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  InputAdornment,
  Dialog,
  Button,
  Slider,
  CircularProgress,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Apartment as OrganizationIcon,
  Info as InfoIcon,
  Notes as NotesIcon,
  Category as CategoryIcon,
  DragIndicator as DragIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Share as ShareIcon,
  LocationOn as LocationIcon,
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  AutoAwesome as AutoAwesomeIcon,
  SmartToyOutlined as ChatAiIcon,
  Save as SaveIcon,
  InfoOutlined as InfoOutlinedIcon,
  ChatBubbleOutline as CommentsIcon,
  ContactMail as ContactMailIcon,
  Map as MapIcon,
  UnfoldMore as UnfoldMoreIcon,
  UnfoldLess as UnfoldLessIcon,
} from "@mui/icons-material";
import { US_STATES } from "../../../data/usStates";
import { formatPhoneNumber } from "../../../utils/phoneFormat";
import ComposeWithMagicDialog from "../../../components/spark/ComposeWithMagicDialog";
import ComposeTextDialog from "../../../components/spark/ComposeTextDialog";
import AddressbookPanelShell from "./AddressbookPanelShell";
import { useSettingsDomain } from "../../../graphQL/settings/scope";
import { useContactCategories } from "../../../components/component-contact/useContactCategories";
import { apiGetSettings } from "../../../graphQL/settings";
import Gate from "../../../components/common/Gate";
import LinkedDocumentsCard from "../../../components/panels/LinkedDocumentsCard";
import { useToggles } from "../../../contexts/FeatureToggleContext";

const EMPTY_ENTRY_BG = "#FFF8DB";
const FILLED_ENTRY_BG = "#FFFFFF";
const getEntryBackground = (value) =>
  String(value ?? "").trim().length > 0 ? FILLED_ENTRY_BG : EMPTY_ENTRY_BG;

const FIELD_HEIGHT = 40;
const FIELD_LABEL_OFFSET = -4; // shift the floating label so it vertically centers on a 40px row
const FIELD_LABEL_SHRINK_TOP = 0;

const getTextFieldStyles = (value) => ({
  "& .MuiInputLabel-root": {
    fontSize: "14px",
    transform: `translate(14px, ${FIELD_LABEL_OFFSET + 16}px) scale(1)`,
  },
  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    fontSize: "12px",
    transform: `translate(14px, ${FIELD_LABEL_SHRINK_TOP - 9}px) scale(0.85)`,
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: getEntryBackground(value),
    borderRadius: "8px",
    fontSize: "14px",
    boxSizing: "border-box",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e0" },
    "&.Mui-focused fieldset": { borderColor: "#4299e1", borderWidth: "1px" },
    // Single-line: lock the row to FIELD_HEIGHT regardless of size="small" vs default
    "&:not(.MuiInputBase-multiline)": {
      height: FIELD_HEIGHT,
      paddingTop: 0,
      paddingBottom: 0,
    },
    "&:not(.MuiInputBase-multiline) input": {
      padding: "0 14px",
      height: "100%",
      boxSizing: "border-box",
    },
    // Multiline: grow naturally, but keep horizontal padding consistent
    "&.MuiInputBase-multiline": {
      padding: "10px 14px",
    },
    "&.MuiInputBase-multiline textarea": {
      padding: 0,
    },
  },
});

const getSelectFieldStyles = (value) => ({
  backgroundColor: getEntryBackground(value),
  borderRadius: "8px",
  fontSize: "14px",
  height: FIELD_HEIGHT,
  boxSizing: "border-box",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e0" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#4299e1", borderWidth: "1px" },
  "& .MuiSelect-select": {
    padding: "0 32px 0 14px",
    height: `${FIELD_HEIGHT}px !important`,
    minHeight: `${FIELD_HEIGHT}px !important`,
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
  },
});

const cardSx = {
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#F8FAFC",
  boxShadow: "none",
  mb: 1.5,
};

const headerBaseSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: 2,
  py: 0.75,
  minHeight: 40,
  cursor: "pointer",
  backgroundColor: "#E5E7EB",
  borderRadius: "8px 8px 0 0",
  "&:hover": { backgroundColor: "#D1D5DB" },
};

const headerTitleSx = {
  color: "#374151",
  fontWeight: 600,
  fontSize: 12.5,
  lineHeight: 1.1,
};

const entryCountSuffixSx = {
  color: "#6B7280",
  fontWeight: 500,
  fontSize: 10.5,
  lineHeight: 1.1,
};

const addBtnSx = {
  color: "#FFFFFF",
  backgroundColor: "#16a34a",
  "&:hover": { backgroundColor: "#15803d" },
  width: 24,
  height: 24,
};

const deleteBtnSx = {
  color: "#9CA3AF",
  "&:hover": { color: "#6B7280", backgroundColor: "#F3F4F6" },
  width: 28,
  height: 28,
};

// Circular pill button for inline "magic email" / "send SMS" inside Phone & Email fields.
const roundActionBtnSx = (bg, hoverBg) => ({
  width: 24,
  height: 24,
  borderRadius: "50%",
  backgroundColor: bg,
  color: "#FFFFFF",
  padding: 0,
  "&:hover": { backgroundColor: hoverBg },
});

const parseListInput = (value) =>
  String(value || "")
    .split(/[,\n;]+/)
    .map((v) => v.trim())
    .filter(Boolean);

const socialMediaTypes = [
  { label: "LinkedIn", value: "linkedin" },
  { label: "X", value: "x" },
  { label: "Instagram", value: "instagram" },
  { label: "Facebook", value: "facebook" },
  { label: "Website", value: "website" },
  { label: "TikTok", value: "tiktok" },
  { label: "YouTube", value: "youtube" },
  { label: "GitHub", value: "github" },
  { label: "WhatsApp", value: "whatsapp" },
  { label: "Telegram", value: "telegram" },
];

const DEFAULT_CARD_ORDER = [
  "contactInterests",
  "person",
  "phones",
  "emails",
  "classification",
  "organization",
  "company",
  "details",
  "notes",
  "socialMedia",
  "addresses",
];

// Maps each collapsible card key to its Settings feature-toggle flag. Cards
// whose flag resolves OFF are hidden and excluded from expand/collapse-all.
const CARD_TOGGLE_FLAGS = {
  contactInterests: "contact.section.interests",
  person: "contact.section.person",
  phones: "contact.section.phones",
  emails: "contact.section.emails",
  classification: "contact.section.classification",
  organization: "contact.section.organization",
  company: "contact.section.company",
  details: "contact.section.details",
  notes: "contact.section.notes",
  socialMedia: "contact.section.social",
  addresses: "contact.section.addresses",
};

const DEFAULT_OPEN_CARDS = { person: true, phones: true, emails: true };
const CONTACT_DETAIL_OPEN_CARDS_KEY = "contact:detail:openCards";

const getSavedOpenCards = () => {
  try {
    const raw = localStorage.getItem(CONTACT_DETAIL_OPEN_CARDS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const LEAFLET_CSS_ID = "leaflet-css";
const LEAFLET_JS_ID = "leaflet-js";
const ensureLeaflet = async () => {
  if (window.L) return window.L;
  if (!document.getElementById(LEAFLET_CSS_ID)) {
    const css = document.createElement("link");
    css.id = LEAFLET_CSS_ID;
    css.rel = "stylesheet";
    css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(css);
  }
  if (!document.getElementById(LEAFLET_JS_ID)) {
    await new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.id = LEAFLET_JS_ID;
      s.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.body.appendChild(s);
    });
  }
  if (!window.L) throw new Error("Leaflet failed to load");
  return window.L;
};

// ── Deep-set helper ──
const deepSet = (obj, path, value) => {
  const clone = JSON.parse(JSON.stringify(obj || {}));
  const keys = path.split(".");
  let cursor = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    if (cursor[keys[i]] == null || typeof cursor[keys[i]] !== "object") {
      cursor[keys[i]] = {};
    }
    cursor = cursor[keys[i]];
  }
  cursor[keys[keys.length - 1]] = value;
  return clone;
};

// ── Reach handlers ──
function useReachHandlers(contactInfo, updateContactInfo) {
  const reach = contactInfo?.reach || {};

  const handleAddPhone = () => {
    updateContactInfo("reach.phones", [...(reach.phones || []), { type: "mobile", value: "" }]);
  };
  const handleUpdatePhone = (index, field, value) => {
    const phones = [...(reach.phones || [])];
    phones[index] = { ...phones[index], [field]: value };
    updateContactInfo("reach.phones", phones);
  };
  const handleDeletePhone = (index) => {
    updateContactInfo("reach.phones", (reach.phones || []).filter((_, i) => i !== index));
  };

  const handleAddEmail = () => {
    updateContactInfo("reach.emails", [...(reach.emails || []), { type: "work", value: "" }]);
  };
  const handleUpdateEmail = (index, field, value) => {
    const emails = [...(reach.emails || [])];
    emails[index] = { ...emails[index], [field]: value };
    updateContactInfo("reach.emails", emails);
  };
  const handleDeleteEmail = (index) => {
    updateContactInfo("reach.emails", (reach.emails || []).filter((_, i) => i !== index));
  };

  const handleAddSocial = () => {
    updateContactInfo("reach.social", [...(reach.social || []), { type: "website", value: "" }]);
  };
  const handleUpdateSocial = (index, field, value) => {
    const social = [...(reach.social || [])];
    social[index] = { ...social[index], [field]: value };
    updateContactInfo("reach.social", social);
  };
  const handleDeleteSocial = (index) => {
    updateContactInfo("reach.social", (reach.social || []).filter((_, i) => i !== index));
  };

  const handleAddAddress = () => {
    updateContactInfo("reach.addresses", [
      ...(reach.addresses || []),
      {
        type: "business",
        address: { line1: "", line2: "", suiteOrUnit: "", city: "", state: "", zip: "", country: "US" },
      },
    ]);
  };
  const handleUpdateAddress = (index, field, value) => {
    const addresses = [...(reach.addresses || [])];
    if (field === "type") {
      addresses[index] = { ...addresses[index], type: value };
    } else {
      addresses[index] = {
        ...addresses[index],
        address: { ...(addresses[index]?.address || {}), [field]: value },
      };
    }
    updateContactInfo("reach.addresses", addresses);
  };
  const handleDeleteAddress = (index) => {
    updateContactInfo("reach.addresses", (reach.addresses || []).filter((_, i) => i !== index));
  };

  return {
    reach,
    handleAddPhone, handleUpdatePhone, handleDeletePhone,
    handleAddEmail, handleUpdateEmail, handleDeleteEmail,
    handleAddSocial, handleUpdateSocial, handleDeleteSocial,
    handleAddAddress, handleUpdateAddress, handleDeleteAddress,
  };
}

function getReachCounts(contactInfo) {
  const reach = contactInfo?.reach || {};
  const hasValue = (item) => String(item?.value ?? "").trim().length > 0;
  const hasAddress = (item) => {
    const a = item?.address;
    if (!a) return false;
    return [a.line1, a.city, a.state, a.zip].some((v) => String(v ?? "").trim().length > 0);
  };
  return {
    phones: (reach.phones || []).filter(hasValue).length,
    emails: (reach.emails || []).filter(hasValue).length,
    social: (reach.social || []).filter(hasValue).length,
    addresses: (reach.addresses || []).filter(hasAddress).length,
  };
}

// ── Reach content components ──

const PhoneContent = ({ contactInfo, updateContactInfo, onComposeText }) => {
  const {
    reach, handleAddPhone, handleUpdatePhone, handleDeletePhone,
  } = useReachHandlers(contactInfo, updateContactInfo);

  return (
    <CardContent sx={{ pt: 2, pb: 2 }}>
      {(reach.phones || []).map((phone, index) => (
        <Box
          key={`phone-${index}`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: index === (reach.phones || []).length - 1 ? 0 : 1,
          }}
        >
          <FormControl sx={{ minWidth: 100 }}>
            <Select
              value={phone.type || "mobile"}
              onChange={(e) => handleUpdatePhone(index, "type", e.target.value)}
              sx={getSelectFieldStyles(phone.type || "mobile")}
              size="small"
            >
              <MenuItem value="mobile">Mobile</MenuItem>
              <MenuItem value="work">Work</MenuItem>
              <MenuItem value="home">Home</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            value={phone.value || ""}
            onChange={(e) => handleUpdatePhone(index, "value", e.target.value)}
            onBlur={(e) => {
              const formatted = formatPhoneNumber(e.target.value || "");
              handleUpdatePhone(index, "value", formatted || e.target.value || "");
            }}
            placeholder="+1 (555) 555-5555"
            variant="outlined"
            size="small"
            sx={getTextFieldStyles(phone.value || "")}
            InputProps={
              phone.value
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Compose text with Magic">
                          <IconButton
                            edge="end"
                            onClick={() => onComposeText?.()}
                            sx={roundActionBtnSx("#7C3AED", "#6D28D9")}
                          >
                            <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
          />
          <IconButton onClick={() => handleDeletePhone(index)} sx={deleteBtnSx}>
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ))}
    </CardContent>
  );
};

const EmailContent = ({ contactInfo, updateContactInfo, onCompose }) => {
  const { reach, handleAddEmail, handleUpdateEmail, handleDeleteEmail } =
    useReachHandlers(contactInfo, updateContactInfo);
  return (
    <CardContent sx={{ pt: 2, pb: 2 }}>
      {(reach.emails || []).map((email, index) => (
        <Box
          key={`email-${index}`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: index === (reach.emails || []).length - 1 ? 0 : 1,
          }}
        >
          <FormControl sx={{ minWidth: 100 }}>
            <Select
              value={email.type || "work"}
              onChange={(e) => handleUpdateEmail(index, "type", e.target.value)}
              sx={getSelectFieldStyles(email.type || "work")}
              size="small"
            >
              <MenuItem value="work">Work</MenuItem>
              <MenuItem value="personal">Personal</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="email"
            value={email.value || ""}
            onChange={(e) => handleUpdateEmail(index, "value", e.target.value)}
            placeholder="email@example.com"
            variant="outlined"
            size="small"
            error={Boolean(email.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value))}
            helperText={email.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value) ? "Invalid email format" : ""}
            sx={getTextFieldStyles(email.value || "")}
            InputProps={
              email.value
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Compose with Magic">
                          <IconButton
                            edge="end"
                            onClick={() => onCompose?.()}
                            sx={roundActionBtnSx("#7C3AED", "#6D28D9")}
                          >
                            <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
          />
          <IconButton onClick={() => handleDeleteEmail(index)} sx={deleteBtnSx}>
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ))}
    </CardContent>
  );
};

const SocialMediaContent = ({ contactInfo, updateContactInfo }) => {
  const { reach, handleAddSocial, handleUpdateSocial, handleDeleteSocial } =
    useReachHandlers(contactInfo, updateContactInfo);

  return (
    <CardContent sx={{ pt: 2, pb: 1 }}>
      {(reach.social || []).map((social, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: index === (reach.social || []).length - 1 ? 0 : 1,
          }}
        >
          <FormControl sx={{ minWidth: 120 }}>
            <Select
              value={social.type || "website"}
              onChange={(e) => handleUpdateSocial(index, "type", e.target.value)}
              sx={getSelectFieldStyles(social.type || "website")}
              size="small"
            >
              {socialMediaTypes.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            value={social.value || ""}
            onChange={(e) => handleUpdateSocial(index, "value", e.target.value)}
            placeholder="https://..."
            variant="outlined"
            size="small"
            sx={getTextFieldStyles(social.value || "")}
          />
          <IconButton onClick={() => handleDeleteSocial(index)} sx={deleteBtnSx}>
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ))}
    </CardContent>
  );
};

const AddressesContent = ({ contactInfo, updateContactInfo }) => {
  const { reach, handleAddAddress, handleUpdateAddress, handleDeleteAddress } =
    useReachHandlers(contactInfo, updateContactInfo);

  const mapStateValue = (stateValue) => {
    const normalized = String(stateValue || "").trim().toLowerCase();
    return US_STATES.find((o) => o.code.toLowerCase() === normalized || o.name.toLowerCase() === normalized) || null;
  };
  const getStateOptionLabel = (option) => {
    if (typeof option === "string") return option;
    return option?.name ? `${option.code} - ${option.name}` : option?.code || "";
  };

  return (
    <CardContent sx={{ pt: 2, pb: 2 }}>
      {(reach.addresses || []).map((addr, index) => (
        <Box key={index} sx={{ mb: 1.5, p: 1.5, backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <FormControl sx={{ minWidth: 120 }}>
              <Select
                value={addr.type || "business"}
                onChange={(e) => handleUpdateAddress(index, "type", e.target.value)}
                sx={getSelectFieldStyles(addr.type || "business")}
                size="small"
              >
                <MenuItem value="business">Business</MenuItem>
                <MenuItem value="personal">Personal</MenuItem>
              </Select>
            </FormControl>
            <IconButton onClick={() => handleDeleteAddress(index)} sx={deleteBtnSx}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <TextField fullWidth label="Address Line 1" value={addr.address?.line1 || ""} onChange={(e) => handleUpdateAddress(index, "line1", e.target.value)} placeholder="Street address" variant="outlined" size="small" sx={getTextFieldStyles(addr.address?.line1 || "")} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address Line 2" value={addr.address?.line2 || ""} onChange={(e) => handleUpdateAddress(index, "line2", e.target.value)} placeholder="Apt, suite (optional)" variant="outlined" size="small" sx={getTextFieldStyles(addr.address?.line2 || "")} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Suite/Unit" value={addr.address?.suiteOrUnit || ""} onChange={(e) => handleUpdateAddress(index, "suiteOrUnit", e.target.value)} variant="outlined" size="small" sx={getTextFieldStyles(addr.address?.suiteOrUnit || "")} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="City" value={addr.address?.city || ""} onChange={(e) => handleUpdateAddress(index, "city", e.target.value)} variant="outlined" size="small" sx={getTextFieldStyles(addr.address?.city || "")} />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                fullWidth
                options={US_STATES}
                value={mapStateValue(addr.address?.state) || (addr.address?.state || null)}
                getOptionLabel={getStateOptionLabel}
                filterOptions={(options, { inputValue }) => {
                  const q = String(inputValue || "").trim().toLowerCase();
                  if (!q) return options;
                  return options.filter((o) =>
                    o.code.toLowerCase().includes(q) || o.name.toLowerCase().includes(q)
                  );
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!value) return false;
                  if (typeof value === "string") {
                    const n = value.trim().toLowerCase();
                    return option.code.toLowerCase() === n || option.name.toLowerCase() === n;
                  }
                  return option.code === value.code;
                }}
                onChange={(_, newValue) => {
                  const stateCode = typeof newValue === "string"
                    ? String(newValue).trim().slice(0, 2).toUpperCase()
                    : (newValue?.code || "");
                  handleUpdateAddress(index, "state", stateCode);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="State" variant="outlined" size="small" sx={getTextFieldStyles(addr.address?.state || "")} />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="ZIP" value={addr.address?.zip || ""} onChange={(e) => handleUpdateAddress(index, "zip", e.target.value)} variant="outlined" size="small" sx={getTextFieldStyles(addr.address?.zip || "")} />
            </Grid>
          </Grid>
        </Box>
      ))}
      <Typography
        variant="caption"
        sx={{ color: "#3B82F6", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
        onClick={handleAddAddress}
      >
        + Address
      </Typography>
    </CardContent>
  );
};

// ── Add buttons for card headers ──
const PhoneAddButton = ({ contactInfo, updateContactInfo, onAfterAdd }) => {
  const { handleAddPhone } = useReachHandlers(contactInfo, updateContactInfo);
  return (
    <IconButton
      size="small"
      onClick={(e) => { e.stopPropagation(); handleAddPhone(); onAfterAdd?.(); }}
      sx={addBtnSx}
      title="Add Phone"
    >
      <AddIcon sx={{ fontSize: 14 }} />
    </IconButton>
  );
};

const EmailAddButton = ({ contactInfo, updateContactInfo, onAfterAdd }) => {
  const { handleAddEmail } = useReachHandlers(contactInfo, updateContactInfo);
  return (
    <IconButton
      size="small"
      onClick={(e) => { e.stopPropagation(); handleAddEmail(); onAfterAdd?.(); }}
      sx={addBtnSx}
      title="Add Email"
    >
      <AddIcon sx={{ fontSize: 14 }} />
    </IconButton>
  );
};

const SocialAddButton = ({ contactInfo, updateContactInfo, onAfterAdd }) => {
  const { handleAddSocial } = useReachHandlers(contactInfo, updateContactInfo);
  return (
    <IconButton
      size="small"
      onClick={(e) => { e.stopPropagation(); handleAddSocial(); onAfterAdd?.(); }}
      sx={addBtnSx}
      title="Add Social"
    >
      <AddIcon sx={{ fontSize: 14 }} />
    </IconButton>
  );
};

const AddressAddButton = ({ contactInfo, updateContactInfo, onAfterAdd }) => {
  const { handleAddAddress } = useReachHandlers(contactInfo, updateContactInfo);
  return (
    <IconButton
      size="small"
      onClick={(e) => { e.stopPropagation(); handleAddAddress(); onAfterAdd?.(); }}
      sx={addBtnSx}
      title="Add Address"
    >
      <AddIcon sx={{ fontSize: 14 }} />
    </IconButton>
  );
};

// ── Main panel ──
const ContactDetailPanel = ({ contact, onClose, onSave, onDelete, onOpenComments, onOpenChat, extraTopContent = null }) => {
  const [contactInfo, setContactInfo] = useState(null);
  const [changedFields, setChangedFields] = useState(new Set());
  const [openCards, setOpenCards] = useState(() => ({ ...DEFAULT_OPEN_CARDS, ...getSavedOpenCards() }));
  const [cardOrder, setCardOrder] = useState(DEFAULT_CARD_ORDER);
  const [dirty, setDirty] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTextOpen, setComposeTextOpen] = useState(false);
  const [interestMapOpen, setInterestMapOpen] = useState(false);
  const [interestLocationsDraft, setInterestLocationsDraft] = useState([]);
  const [mapSearch, setMapSearch] = useState("");
  const [mapSearching, setMapSearching] = useState(false);
  const [mapSearchError, setMapSearchError] = useState("");
  const settingsDomain = useSettingsDomain();
  const sharedCategories = useContactCategories();
  const categoryOptions = sharedCategories.filter((cat) => (cat?.isActive ?? true));
  const [propertyCategoryOptions, setPropertyCategoryOptions] = useState([]);
  const [propertyStatusOptions, setPropertyStatusOptions] = useState([]);
  const getCategoryDisplayLabel = useCallback((value) => {
    const raw = String(value || "").trim();
    if (!raw) return "N/A";
    const match = categoryOptions.find((cat) => {
      const candidates = [cat?.value, cat?.id, cat?.name, cat?.label].map((v) => String(v || "").trim());
      return candidates.includes(raw);
    });
    return String(match?.label || match?.name || raw);
  }, [categoryOptions]);
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const markerLayerRef = useRef(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => {
    const loadPropertySettings = async () => {
      if (!settingsDomain) {
        setPropertyCategoryOptions([]);
        setPropertyStatusOptions([]);
        return;
      }
      try {
        const [categoryRes, statusRes] = await Promise.all([
          apiGetSettings({ schemaType: "category", type: "property", domain: settingsDomain }),
          apiGetSettings({ schemaType: "status", type: "property", domain: settingsDomain }),
        ]);
        const pickLatestSetting = (settings) => {
          if (!Array.isArray(settings) || settings.length === 0) return null;
          return settings.reduce((latest, current) => {
            const latestTs = Number(latest?.updatedAt ?? latest?.createdAt ?? 0);
            const currentTs = Number(current?.updatedAt ?? current?.createdAt ?? 0);
            return currentTs >= latestTs ? current : latest;
          }, settings[0]);
        };
        const extractSettingDataArray = (setting) => {
          const payload = setting?.data;
          if (Array.isArray(payload)) return payload;
          if (Array.isArray(payload?.data)) return payload.data;
          return [];
        };
        const categories = extractSettingDataArray(pickLatestSetting(categoryRes?.settings)).filter((x) => x && x.isActive !== false);
        const statuses = extractSettingDataArray(pickLatestSetting(statusRes?.settings)).filter((x) => x && x.isActive !== false);
        setPropertyCategoryOptions(categories);
        setPropertyStatusOptions(statuses);
      } catch (error) {
        console.error("Failed to load property interest settings:", error);
        setPropertyCategoryOptions([]);
        setPropertyStatusOptions([]);
      }
    };
    loadPropertySettings();
  }, [settingsDomain]);

  useEffect(() => {
    if (!contact) {
      setContactInfo(null);
      return;
    }
    // Re-sync on object identity too — the parent hydrates slim list rows
    // with the full document shortly after selection — but never clobber
    // in-progress edits on the same contact.
    const sameContact = Boolean(contactInfo?.id && contact?.id && contactInfo.id === contact.id);
    if (sameContact && dirty) return;
    setContactInfo(JSON.parse(JSON.stringify(contact)));
    if (!sameContact) {
      setDirty(false);
      setChangedFields(new Set());
      setOpenCards((prev) => ({ ...DEFAULT_OPEN_CARDS, ...prev }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact]);

  useEffect(() => {
    try {
      localStorage.setItem(CONTACT_DETAIL_OPEN_CARDS_KEY, JSON.stringify(openCards || {}));
    } catch {}
  }, [openCards]);

  // Ensure new cards appear even when component state persists across hot reloads.
  useEffect(() => {
    setCardOrder((prev) => {
      const base = Array.isArray(prev) ? [...prev] : [...DEFAULT_CARD_ORDER];
      const next = base.flatMap((key) => (key === "phoneEmail" ? ["phones", "emails"] : [key]));
      if (!next.includes("phones")) next.splice(1, 0, "phones");
      if (!next.includes("emails")) next.splice(2, 0, "emails");
      if (!next.includes("contactInterests")) next.unshift("contactInterests");
      const interestIndex = next.indexOf("contactInterests");
      if (interestIndex > 0) {
        next.splice(interestIndex, 1);
        next.unshift("contactInterests");
      }
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!interestMapOpen || !mapElRef.current) return undefined;
    (async () => {
      const L = await ensureLeaflet();
      if (cancelled || !mapElRef.current) return;
      if (!mapRef.current) {
        mapRef.current = L.map(mapElRef.current).setView([37.36, -121.93], 9);
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution: "&copy; OpenStreetMap contributors",
        }).addTo(mapRef.current);
        markerLayerRef.current = L.layerGroup().addTo(mapRef.current);
        mapRef.current.on("click", (e) => {
          const { lat, lng } = e.latlng;
          setInterestLocationsDraft((prev) => [...prev, { latitude: lat, longitude: lng, radiusMiles: 5 }]);
        });
      } else {
        mapRef.current.invalidateSize();
      }
      setTimeout(() => mapRef.current?.invalidateSize(), 120);
    })();
    return () => {
      cancelled = true;
    };
  }, [interestMapOpen]);

  useEffect(() => {
    if (!interestMapOpen || !mapRef.current || !markerLayerRef.current || !window.L) return;
    const L = window.L;
    markerLayerRef.current.clearLayers();
    const bounds = L.latLngBounds([]);
    interestLocationsDraft.forEach((loc, idx) => {
      const lat = Number(loc?.latitude);
      const lng = Number(loc?.longitude);
      const rMiles = Number(loc?.radiusMiles) || 1;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const marker = L.marker([lat, lng]).addTo(markerLayerRef.current);
      marker.bindTooltip(`Pin ${idx + 1}`);
      L.circle([lat, lng], {
        radius: rMiles * 1609.34,
        color: "#2563EB",
        fillColor: "#93C5FD",
        fillOpacity: 0.2,
        weight: 1.5,
      }).addTo(markerLayerRef.current);
      bounds.extend([lat, lng]);
    });
    if (bounds.isValid()) mapRef.current.fitBounds(bounds.pad(0.25));
  }, [interestMapOpen, interestLocationsDraft]);

  const handleMapSearch = useCallback(async () => {
    const q = String(mapSearch || "").trim();
    if (!q || !mapRef.current) return;
    setMapSearching(true);
    setMapSearchError("");
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`Search failed (${res.status})`);
      const rows = await res.json();
      const first = Array.isArray(rows) ? rows[0] : null;
      const lat = Number(first?.lat);
      const lon = Number(first?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        throw new Error("Address not found");
      }
      mapRef.current.setView([lat, lon], 12, { animate: true });
      setInterestLocationsDraft((prev) => [...prev, { latitude: lat, longitude: lon, radiusMiles: 5 }]);
    } catch (err) {
      setMapSearchError(err?.message || "Failed to locate address");
    } finally {
      setMapSearching(false);
    }
  }, [mapSearch]);

  const updateContactInfo = useCallback((path, value) => {
    setContactInfo((prev) => {
      const updated = deepSet(prev, path, value);
      return updated;
    });
    setChangedFields((prev) => new Set(prev).add(path.split(".")[0]));
    setDirty(true);
  }, []);

  const handleSave = useCallback(() => {
    if (contactInfo && onSave) {
      const emails = contactInfo?.reach?.emails || [];
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalid = emails.filter((e) => e?.value && !emailRegex.test(String(e.value).trim()));
      if (invalid.length) {
        window.alert(`Cannot save: ${invalid.length} invalid email address(es). Fix or remove them first.`);
        return;
      }
      const changes = {};
      for (const field of changedFields) {
        changes[field] = contactInfo[field];
      }
      onSave(changes);
      setDirty(false);
      setChangedFields(new Set());
    }
  }, [contactInfo, changedFields, onSave]);

  const handlePanelClose = useCallback(() => {
    if (dirty) {
      const confirmed = window.confirm("You have unsaved changes. Close and lose your changes?");
      if (!confirmed) return;
    }
    onClose?.();
  }, [dirty, onClose]);

  const { isOn } = useToggles();
  const toggle = (key) => setOpenCards((prev) => ({ ...prev, [key]: !prev[key] }));
  const openCard = (key) => setOpenCards((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  const isOpen = (key) => !!openCards[key];
  const allCardsClosed = cardOrder.every((key) => !isOpen(key));

  // Cards currently visible per the Settings feature toggles. Expand/collapse-all
  // only acts on these.
  const visibleCardKeys = cardOrder.filter((key) => isOn(CARD_TOGGLE_FLAGS[key]));
  const allVisibleOpen =
    visibleCardKeys.length > 0 && visibleCardKeys.every((key) => isOpen(key));
  const handleToggleAllCards = () => {
    const target = !allVisibleOpen;
    setOpenCards((prev) => {
      const next = { ...prev };
      visibleCardKeys.forEach((key) => { next[key] = target; });
      return next;
    });
  };

  // Double-click on any header opens/closes ALL cards based on that card's
  // current state (closed → open all; open → close all). Single click keeps
  // its normal toggle behavior via a short delay so dblclick can pre-empt it.
  const clickTimerRef = useRef(null);
  const handleHeaderClick = (key) => () => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      toggle(key);
    }, 220);
  };
  const handleHeaderDoubleClick = (key) => (e) => {
    e.stopPropagation();
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    setOpenCards((prev) => {
      const targetOpen = !prev[key];
      const next = { ...prev };
      cardOrder.forEach((k) => { next[k] = targetOpen; });
      return next;
    });
  };

  const handleDragStart = (e, idx) => {
    if (!allCardsClosed) { e.preventDefault(); return; }
    dragItem.current = idx;
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (idx) => {
    if (!allCardsClosed) return;
    dragOverItem.current = idx;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    if (!allCardsClosed) return;
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current === dragOverItem.current) return;
    setCardOrder((prev) => {
      const next = [...prev];
      const [removed] = next.splice(dragItem.current, 1);
      next.splice(dragOverItem.current, 0, removed);
      return next;
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragOver = (e) => {
    if (!allCardsClosed) return;
    e.preventDefault();
  };

  // ── Header renderers ──
  const renderHeader = (key, icon, label, rightAction = null) => (
    <Box
      onClick={handleHeaderClick(key)}
      onDoubleClick={handleHeaderDoubleClick(key)}
      title="Double-click to open/close all"
      sx={{
        ...headerBaseSx,
        ...(isOpen(key)
          ? { borderBottom: "1px solid #e2e8f0", borderRadius: "8px 8px 0 0" }
          : { borderRadius: "8px" }),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1 }}>
        <DragIcon
          sx={{
            fontSize: 16,
            color: "#9CA3AF",
            cursor: allCardsClosed ? "grab" : "default",
            mr: 0.25,
            opacity: allCardsClosed ? 1 : 0.45,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        />
        {icon}
        <Typography sx={headerTitleSx}>{label}</Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {rightAction}
        {isOpen(key) ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
      </Box>
    </Box>
  );

  const renderHeaderWithAction = (key, icon, label, count, actionButton) => (
    <Box
      onClick={handleHeaderClick(key)}
      onDoubleClick={handleHeaderDoubleClick(key)}
      title="Double-click to open/close all"
      sx={{
        ...headerBaseSx,
        ...(isOpen(key)
          ? { borderBottom: "1px solid #e2e8f0", borderRadius: "8px 8px 0 0" }
          : { borderRadius: "8px" }),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1 }}>
        <DragIcon
          sx={{
            fontSize: 16,
            color: "#9CA3AF",
            cursor: allCardsClosed ? "grab" : "default",
            mr: 0.25,
            opacity: allCardsClosed ? 1 : 0.45,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        />
        {icon}
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
          <Typography sx={headerTitleSx}>{label}</Typography>
          {count > 0 && (
            <Typography sx={entryCountSuffixSx}>
              - {count} entr{count === 1 ? "y" : "ies"}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {actionButton}
        {isOpen(key) ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
      </Box>
    </Box>
  );

  const identity = contactInfo?.identity || {};
  const person = identity.person || {};
  const organization = identity.organization || {};
  const company = identity.company || {};
  const fullName = contact
    ? `${person.firstName || ""} ${person.lastName || ""}`.trim()
    : "";

  const headerTitle = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1 }}>
      <InfoOutlinedIcon sx={{ fontSize: 16, color: "#1D4ED8" }} />
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>
        Details
      </Typography>
    </Box>
  );
  const detailEntryCount = cardOrder.length;

  const headerActions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <IconButton
        size="small"
        onClick={handleSave}
        disabled={!dirty}
        title={dirty ? "Save changes" : "No changes to save"}
        sx={{
          width: 28,
          height: 28,
          backgroundColor: dirty ? "#16a34a" : "#94A3B8",
          color: "#FFFFFF",
          "&:hover": { backgroundColor: dirty ? "#15803d" : "#94A3B8" },
          "&.Mui-disabled": {
            backgroundColor: "#94A3B8",
            color: "#FFFFFF",
            opacity: 1,
          },
        }}
      >
        <SaveIcon sx={{ fontSize: 14 }} />
      </IconButton>
      <Tooltip title={allVisibleOpen ? "Collapse all" : "Expand all"}>
        <IconButton
          size="small"
          onClick={handleToggleAllCards}
          data-shell-muted="true"
          sx={{ width: 24, height: 24 }}
        >
          {allVisibleOpen
            ? <UnfoldLessIcon sx={{ fontSize: 15 }} />
            : <UnfoldMoreIcon sx={{ fontSize: 15 }} />}
        </IconButton>
      </Tooltip>
    </Box>
  );

  // ── No contact selected ──
  if (!contact) {
    return (
      <AddressbookPanelShell
        onClose={handlePanelClose}
        titleContent={headerTitle}
        headerActions={headerActions}
      >
        <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
          <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
            Select a contact to view details
          </Typography>
        </Box>
      </AddressbookPanelShell>
    );
  }

  if (!contactInfo) return null;

  const getSelectSx = (value) => ({
    backgroundColor: getEntryBackground(value),
    borderRadius: "8px",
    fontSize: "14px",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e0" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#4299e1", borderWidth: "1px" },
  });

  // ── Card renderers ──
  const cardRenderers = {
    classification: () => (
      <Card sx={cardSx} key="classification">
        {renderHeader("classification", <CategoryIcon sx={{ fontSize: 16, color: "#EC4899" }} />, "Classification")}
        <Collapse in={isOpen("classification")}>
          <CardContent sx={{ pt: 2, pb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel shrink>Status</InputLabel>
                  <Select
                    label="Status"
                    value={contactInfo?.status || ""}
                    onChange={(e) => updateContactInfo("status", e.target.value)}
                    sx={getSelectSx(contactInfo?.status || "")}
                    renderValue={(value) => (value ? String(value) : "N/A")}
                  >
                    <MenuItem value="">N/A</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="lead">Lead</MenuItem>
                    <MenuItem value="prospect">Prospect</MenuItem>
                    <MenuItem value="client">Client</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel shrink>Contact Type</InputLabel>
                  <Select
                    label="Contact Type"
                    value={contactInfo?.category || ""}
                    onChange={(e) => updateContactInfo("category", e.target.value)}
                    sx={getSelectSx(contactInfo?.category || "")}
                    renderValue={(value) => getCategoryDisplayLabel(value)}
                  >
                    <MenuItem value="">N/A</MenuItem>
                    {categoryOptions.map((category) => {
                      const optionValue = String(
                        category?.value || category?.id || category?.name || category?.label || ""
                      ).trim();
                      const optionLabel = String(
                        category?.label || category?.name || category?.value || category?.id || ""
                      ).trim();
                      if (!optionValue || !optionLabel) return null;
                      return (
                        <MenuItem key={optionValue} value={optionValue}>{optionLabel}</MenuItem>
                      );
                    })}
                    {categoryOptions.length === 0 && (
                      <MenuItem disabled value="__no_categories__">No active contact types</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>
    ),

    person: () => (
      <Card sx={cardSx} key="person">
        {renderHeader("person", <PersonIcon sx={{ fontSize: 16, color: "#3B82F6" }} />, "Person")}
        <Collapse in={isOpen("person")}>
          <CardContent sx={{ pt: 2, pb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="First Name" value={person.firstName || ""} onChange={(e) => updateContactInfo("identity.person.firstName", e.target.value)} placeholder="John" variant="outlined" sx={getTextFieldStyles(person.firstName)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Last Name" value={person.lastName || ""} onChange={(e) => updateContactInfo("identity.person.lastName", e.target.value)} placeholder="Smith" variant="outlined" sx={getTextFieldStyles(person.lastName)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Nickname" value={person.nickname || ""} onChange={(e) => updateContactInfo("identity.person.nickname", e.target.value)} placeholder="Johnny" variant="outlined" sx={getTextFieldStyles(person.nickname)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Title" value={person.title || ""} onChange={(e) => updateContactInfo("identity.person.title", e.target.value)} placeholder="Senior Broker" variant="outlined" sx={getTextFieldStyles(person.title)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Role" value={person.role || ""} onChange={(e) => updateContactInfo("identity.person.role", e.target.value)} placeholder="Broker" variant="outlined" sx={getTextFieldStyles(person.role)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Tagline" value={person.tagline || ""} onChange={(e) => updateContactInfo("identity.person.tagline", e.target.value)} placeholder="Fast comps, clean underwriting..." variant="outlined" sx={getTextFieldStyles(person.tagline)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Languages" value={person.languages || ""} onChange={(e) => updateContactInfo("identity.person.languages", e.target.value)} placeholder="English, Spanish" variant="outlined" sx={getTextFieldStyles(person.languages)} />
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>
    ),

    company: () => (
      <Card sx={cardSx} key="company">
        {renderHeader("company", <BusinessIcon sx={{ fontSize: 16, color: "#10B981" }} />, "Company")}
        <Collapse in={isOpen("company")}>
          <CardContent sx={{ pt: 2, pb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Company Name" value={company.name || ""} onChange={(e) => updateContactInfo("identity.company.name", e.target.value)} placeholder="Acme Commercial Realty" variant="outlined" sx={getTextFieldStyles(company.name)} />
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>
    ),
    organization: () => (
      <Card sx={cardSx} key="organization">
        {renderHeader("organization", <OrganizationIcon sx={{ fontSize: 16, color: "#0EA5E9" }} />, "Organization")}
        <Collapse in={isOpen("organization")}>
          <CardContent sx={{ pt: 2, pb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Organization Name"
                  value={organization.name || ""}
                  onChange={(e) => updateContactInfo("identity.organization.name", e.target.value)}
                  placeholder="Rouss Capital"
                  variant="outlined"
                  sx={getTextFieldStyles(organization.name)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Department"
                  value={organization.department || ""}
                  onChange={(e) => updateContactInfo("identity.organization.department", e.target.value)}
                  placeholder="Acquisitions"
                  variant="outlined"
                  sx={getTextFieldStyles(organization.department)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Team"
                  value={organization.team || ""}
                  onChange={(e) => updateContactInfo("identity.organization.team", e.target.value)}
                  placeholder="West Region"
                  variant="outlined"
                  sx={getTextFieldStyles(organization.team)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Organization Website"
                  value={organization.website || ""}
                  onChange={(e) => updateContactInfo("identity.organization.website", e.target.value)}
                  placeholder="https://example.com"
                  variant="outlined"
                  sx={getTextFieldStyles(organization.website)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>
    ),

    details: () => (
      <Card sx={cardSx} key="details">
        {renderHeader("details", <InfoIcon sx={{ fontSize: 16, color: "#8B5CF6" }} />, "Details")}
        <Collapse in={isOpen("details")}>
          <CardContent sx={{ pt: 2, pb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Specialties" value={contactInfo?.data?.specialties || ""} onChange={(e) => updateContactInfo("data.specialties", e.target.value)} placeholder="Retail, Office, Investment Sales" variant="outlined" sx={getTextFieldStyles(contactInfo?.data?.specialties)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Markets" value={contactInfo?.data?.markets || ""} onChange={(e) => updateContactInfo("data.markets", e.target.value)} placeholder="San Francisco, Oakland, San Jose" variant="outlined" sx={getTextFieldStyles(contactInfo?.data?.markets)} />
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>
    ),

    contactInterests: () => {
      const interests =
        (contactInfo?.data && typeof contactInfo.data === "object" && contactInfo.data.interests) ||
        contactInfo?.interests ||
        {};
      const geoLocations = Array.isArray(interests?.geoLocations) ? interests.geoLocations : [];
      return (
        <Card sx={cardSx} key="contactInterests">
          {renderHeader(
            "contactInterests",
            <ContactMailIcon sx={{ fontSize: 16, color: "#0EA5E9" }} />,
            "Contact Interests",
            <IconButton
              size="small"
              title="Map interests"
              onClick={(e) => {
                e.stopPropagation();
                const rows = geoLocations.map((g) => ({
                  latitude: Number(g?.latitude) || 0,
                  longitude: Number(g?.longitude) || 0,
                  radiusMiles: Number(g?.radiusMiles) || 5,
                }));
                setInterestLocationsDraft(rows);
                setInterestMapOpen(true);
              }}
              sx={{ width: 24, height: 24 }}
            >
              <MapIcon sx={{ fontSize: 15, color: "#0EA5E9" }} />
            </IconButton>
          )}
          <Collapse in={isOpen("contactInterests")}>
            <CardContent sx={{ pt: 2, pb: 2 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={interests?.status || ""}
                      onChange={(e) => updateContactInfo("data.interests.status", e.target.value)}
                      sx={getSelectFieldStyles(interests?.status || "")}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {propertyStatusOptions.map((s, idx) => {
                        const statusValue = s?.label || s?.name || s?.value || s?.id || "";
                        return (
                          <MenuItem key={s?.id || s?.value || s?.label || `status-${idx}`} value={statusValue}>
                            {statusValue}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      label="Category"
                      value={interests?.category || ""}
                      onChange={(e) => updateContactInfo("data.interests.category", e.target.value)}
                      sx={getSelectFieldStyles(interests?.category || "")}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {propertyCategoryOptions.map((c, idx) => {
                        const categoryValue = c?.id || c?.value || c?.label || c?.name || "";
                        const categoryLabel = c?.label || c?.name || c?.value || c?.id || "";
                        return (
                          <MenuItem key={c?.id || c?.value || c?.label || `category-${idx}`} value={categoryValue}>
                            {categoryLabel}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Price Range Min"
                    value={interests?.priceMin || ""}
                    onChange={(e) => updateContactInfo("data.interests.priceMin", e.target.value)}
                    variant="outlined"
                    sx={getTextFieldStyles(interests?.priceMin)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Price Range Max"
                    value={interests?.priceMax || ""}
                    onChange={(e) => updateContactInfo("data.interests.priceMax", e.target.value)}
                    variant="outlined"
                    sx={getTextFieldStyles(interests?.priceMax)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Cities"
                    value={Array.isArray(interests?.geoCities) ? interests.geoCities.join(", ") : interests?.geoCities || ""}
                    onChange={(e) => updateContactInfo("data.interests.geoCities", parseListInput(e.target.value))}
                    helperText="Use commas, semicolons, or new lines (e.g., Los Angeles, San Diego)"
                    variant="outlined"
                    sx={getTextFieldStyles(interests?.geoCities)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Zipcodes"
                    value={Array.isArray(interests?.geoZipcodes) ? interests.geoZipcodes.join(", ") : interests?.geoZipcodes || ""}
                    onChange={(e) => updateContactInfo("data.interests.geoZipcodes", parseListInput(e.target.value))}
                    helperText="Use commas, semicolons, or new lines (e.g., 94105, 90012)"
                    variant="outlined"
                    sx={getTextFieldStyles(interests?.geoZipcodes)}
                  />
                </Grid>
                {geoLocations.length > 0 && (
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: 11, color: "#64748B" }}>
                      {geoLocations.length} map pin{geoLocations.length > 1 ? "s" : ""} with radius saved
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Collapse>
        </Card>
      );
    },

    notes: () => (
      <Card sx={cardSx} key="notes">
        {renderHeader("notes", <NotesIcon sx={{ fontSize: 16, color: "#F59E0B" }} />, "Notes")}
        <Collapse in={isOpen("notes")}>
          <CardContent sx={{ pt: 2, pb: 2 }}>
            <TextField fullWidth multiline rows={4} label="Notes" value={identity.notes || ""} onChange={(e) => updateContactInfo("identity.notes", e.target.value)} placeholder="Met via LoopNet..." variant="outlined" sx={getTextFieldStyles(identity.notes)} />
          </CardContent>
        </Collapse>
      </Card>
    ),

    phones: () => {
      const counts = getReachCounts(contactInfo);
      return (
        <Card sx={cardSx} key="phones">
          {renderHeaderWithAction(
            "phones",
            <PhoneIcon sx={{ fontSize: 16, color: "#3B82F6" }} />,
            "Phone",
            counts.phones,
            <PhoneAddButton contactInfo={contactInfo} updateContactInfo={updateContactInfo} onAfterAdd={() => openCard("phones")} />
          )}
          <Collapse in={isOpen("phones")}>
            <PhoneContent
              contactInfo={contactInfo}
              updateContactInfo={updateContactInfo}
              onComposeText={() => setComposeTextOpen(true)}
            />
          </Collapse>
        </Card>
      );
    },
    emails: () => {
      const counts = getReachCounts(contactInfo);
      return (
        <Card sx={cardSx} key="emails">
          {renderHeaderWithAction(
            "emails",
            <EmailIcon sx={{ fontSize: 16, color: "#3B82F6" }} />,
            "Email",
            counts.emails,
            <EmailAddButton contactInfo={contactInfo} updateContactInfo={updateContactInfo} onAfterAdd={() => openCard("emails")} />
          )}
          <Collapse in={isOpen("emails")}>
            <EmailContent
              contactInfo={contactInfo}
              updateContactInfo={updateContactInfo}
              onCompose={() => setComposeOpen(true)}
            />
          </Collapse>
        </Card>
      );
    },

    socialMedia: () => {
      const counts = getReachCounts(contactInfo);
      return (
        <Card sx={cardSx} key="socialMedia">
          {renderHeaderWithAction(
            "socialMedia",
            <ShareIcon sx={{ fontSize: 16, color: "#8B5CF6" }} />,
            "Social Media",
            counts.social,
            <SocialAddButton contactInfo={contactInfo} updateContactInfo={updateContactInfo} onAfterAdd={() => openCard("socialMedia")} />
          )}
          <Collapse in={isOpen("socialMedia")}>
            <SocialMediaContent contactInfo={contactInfo} updateContactInfo={updateContactInfo} />
          </Collapse>
        </Card>
      );
    },

    addresses: () => {
      const counts = getReachCounts(contactInfo);
      return (
        <Card sx={cardSx} key="addresses">
          {renderHeaderWithAction(
            "addresses",
            <LocationIcon sx={{ fontSize: 16, color: "#10B981" }} />,
            "Addresses",
            counts.addresses,
            <AddressAddButton contactInfo={contactInfo} updateContactInfo={updateContactInfo} onAfterAdd={() => openCard("addresses")} />
          )}
          <Collapse in={isOpen("addresses")}>
            <AddressesContent contactInfo={contactInfo} updateContactInfo={updateContactInfo} />
          </Collapse>
        </Card>
      );
    },
  };

  return (
    <>
    <AddressbookPanelShell
      onClose={handlePanelClose}
      titleContent={headerTitle}
      headerActions={headerActions}
      footerLeft={
        fullName ? (
          <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
            {fullName}
          </Typography>
        ) : null
      }
      footerRight={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
            {detailEntryCount} entr{detailEntryCount === 1 ? "y" : "ies"}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onDelete?.(contactInfo.id)}
            title="Delete contact"
            sx={{ color: "#9CA3AF", "&:hover": { color: "#6B7280", backgroundColor: "#F3F4F6" } }}
          >
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      }
    >
      <Box sx={{ pt: 0.75 }}>
        {extraTopContent ? <Box sx={{ mb: 1 }}>{extraTopContent}</Box> : null}
        {cardOrder.map((key, idx) => {
          const renderer = cardRenderers[key];
          if (!renderer) return null;
          return (
            <Gate key={key} flag={CARD_TOGGLE_FLAGS[key]}>
              <Box
                draggable={allCardsClosed}
                onDragStart={allCardsClosed ? (e) => handleDragStart(e, idx) : undefined}
                onDragEnter={allCardsClosed ? () => handleDragEnter(idx) : undefined}
                onDragEnd={allCardsClosed ? handleDragEnd : undefined}
                onDragOver={allCardsClosed ? handleDragOver : undefined}
              >
                {renderer()}
              </Box>
            </Gate>
          );
        })}
        {contactInfo?.id ? (
          <Box sx={{ mt: 1 }}>
            <LinkedDocumentsCard refCollection="contacts" refId={contactInfo.id} />
          </Box>
        ) : null}
      </Box>
    </AddressbookPanelShell>

    <ComposeWithMagicDialog
      open={composeOpen}
      onClose={() => setComposeOpen(false)}
      contact={contactInfo}
    />
    <ComposeTextDialog
      open={composeTextOpen}
      onClose={() => setComposeTextOpen(false)}
      contact={contactInfo}
    />
    <Dialog open={interestMapOpen} onClose={() => setInterestMapOpen(false)} fullScreen>
      <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", height: "100%" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>Contact Interest Map</Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter address, city, or zip code"
            value={mapSearch}
            onChange={(e) => setMapSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleMapSearch(); }}
          />
          <Button variant="contained" onClick={handleMapSearch} disabled={mapSearching}>
            {mapSearching ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Locate + Pin"}
          </Button>
        </Box>
        {mapSearchError ? (
          <Typography sx={{ fontSize: 12, color: "#DC2626", mb: 1 }}>{mapSearchError}</Typography>
        ) : null}
        <Box ref={mapElRef} sx={{ width: "100%", flex: 1, minHeight: 420, borderRadius: 1, border: "1px solid #E2E8F0", mb: 1 }} />
        <Typography sx={{ fontSize: 11, color: "#64748B", mb: 1 }}>
          Click map to drop pins. Set a radius for each pin.
        </Typography>
        {interestLocationsDraft.map((loc, idx) => (
          <Box key={`geo-${idx}`} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography sx={{ fontSize: 12, minWidth: 64 }}>Pin {idx + 1}</Typography>
            <Slider
              value={Number(loc.radiusMiles) || 1}
              min={1}
              max={50}
              step={1}
              onChange={(_, v) => {
                const radiusMiles = Array.isArray(v) ? v[0] : v;
                setInterestLocationsDraft((prev) => prev.map((p, i) => (i === idx ? { ...p, radiusMiles } : p)));
              }}
              sx={{ flex: 1 }}
            />
            <Typography sx={{ fontSize: 12, minWidth: 56 }}>{Math.round(loc.radiusMiles || 0)} mi</Typography>
            <Button size="small" color="error" onClick={() => setInterestLocationsDraft((prev) => prev.filter((_, i) => i !== idx))}>
              Remove
            </Button>
          </Box>
        ))}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button color="error" onClick={() => setInterestLocationsDraft([])}>Clear Pins</Button>
          <Button onClick={() => setInterestMapOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              updateContactInfo("data.interests.geoLocations", interestLocationsDraft);
              setInterestMapOpen(false);
            }}
          >
            Save Map Interests
          </Button>
        </Box>
      </Box>
    </Dialog>
    </>
  );
};

export default ContactDetailPanel;
